import { useState, useEffect } from 'react';
import { useOrganization } from './useOrganization';
import { useExperiments } from './useExperiments';
import { supabase } from '@/integrations/supabase/client';
import * as Sentry from '@sentry/react';

interface MetabaseReview {
  id: string;
  user_id: string;
  approve_public_use: string;
  display_name: string;
  survey_results_at: string;
  review: string;
  rating: string;
  would_you_recommend_the_product: string;
  social_media_links: string | string[];
  videos: string | string[];
  experiment_name: string;
  user_tier: string;
  allow_video_market_use: boolean;
}

interface VideoObject {
  thumbnailUrl: string;
  path: string;
}

interface ProcessedReview {
  id: string;
  userId: string;
  name: string;
  initials: string;
  content: string;
  rating: number;
  isAnonymous: boolean;
  isInfluencer: boolean;
  experimentName: string;
  socialMediaUrls: string[];
  videos: VideoObject[];
  wouldRecommend: boolean;
  allowVideoUse: boolean;
  submittedAt?: string;
}

export const useReviews = () => {
  const [reviews, setReviews] = useState<ProcessedReview[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { organization } = useOrganization();
  const { data: experiments } = useExperiments();

  useEffect(() => {
    const fetchReviews = async () => {
      if (!organization || !experiments?.length) {
        setReviews([]);
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        const experimentIds = experiments.map(exp => exp.id);

        const { data: response, error: apiError } = await supabase.functions.invoke('metabase-query/78', {
          body: {
            parameters: [
              {
                type: "id",
                target: ["dimension", ["template-tag", "experiment_ids"]],
                value: experimentIds
              }
            ],
            format_rows: true,
            pivot_results: false
          }
        });

        if (apiError) {
          throw new Error(`Failed to fetch reviews: ${apiError.message}`);
        }

        const data: MetabaseReview[] = response;

        // Process and transform the data
        const processedReviews: ProcessedReview[] = data
          .filter(review =>
            review.would_you_recommend_the_product ||
            review.review
          )
          .map(review => {
            const isPublicName = review.approve_public_use === 'true';
            const isInfluencer = review.user_tier !== 'DEFAULT';
            const displayName = review.display_name;
            const name = isPublicName ? (displayName && displayName.trim() ? displayName.trim() : 'Anonymous') : 'Anonymous';
            const initials = name === 'Anonymous' ? 'A' : getInitials(name);
            const wouldRecommend = review.would_you_recommend_the_product === 'true';
            const allowVideoUse = review.allow_video_market_use;

            // Get actual review content
            const content = review.review || '';

            return {
              id: review.id,
              userId: review.user_id,
              name,
              initials,
              content,
              rating: parseInt(review.rating) || 5,
              isAnonymous: !isPublicName,
              isInfluencer,
              experimentName: review.experiment_name,
              socialMediaUrls: getArray(review.social_media_links) as string[],
              videos: extractVideoObjects(getArray(review.videos)) as VideoObject[],
              wouldRecommend,
              allowVideoUse,
              submittedAt: review.survey_results_at
            };
          });

        setReviews(processedReviews);
      } catch (err) {
        Sentry.captureException(err);
        setError(err instanceof Error ? err.message : 'Failed to fetch reviews');
      } finally {
        setLoading(false);
      }
    };

    fetchReviews();
  }, [organization, experiments]);

  return { reviews, loading, error };
};

// Extract all URLs from string | array | JSON-string
function getArray(input: string | string[] | null | undefined): unknown[] {
  if (!input) return [];
  if (Array.isArray(input)) return input;
  if (typeof input === 'string') {
    return JSON.parse(input);
  }
  return [];
}

// Extract video objects from string | array | JSON-string
function extractVideoObjects(input: unknown[] | null | undefined): VideoObject[] {
  if (!input) return [];

  return input
    .filter((item): item is { path: string; thumbnail: string } =>
      item && typeof item === 'object' &&
      'path' in item && 'thumbnail' in item &&
      typeof (item as { path: unknown }).path === 'string' &&
      typeof (item as { thumbnail: unknown }).thumbnail === 'string'
    )
    .map(item => ({
      thumbnailUrl: item.thumbnail,
      path: item.path
    }));
}

// Helper function to get initials from name
const getInitials = (name: string): string => {
  return name.split(' ').map(word => word[0]).join('').toUpperCase();
};